import React from 'react';
import { Link } from 'react-router-dom';
import {
  InfoCircleOutlined,
  LockOutlined,
  RobotOutlined,
  SafetyOutlined,
  BarChartOutlined,
  SearchOutlined,
  DatabaseOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  CrownOutlined,
  SettingOutlined,
  UserOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  TeamOutlined,
  CloudSyncOutlined,
  FundOutlined,
  FileTextOutlined,
  SyncOutlined,
  PartitionOutlined,
} from '@ant-design/icons';
import { pageShellStyle } from '../theme';
import './About.css';

const ICONS = {
  LockOutlined,
  RobotOutlined,
  SafetyOutlined,
  BarChartOutlined,
  SearchOutlined,
  DatabaseOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  CrownOutlined,
  SettingOutlined,
  UserOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  CloudSyncOutlined,
  FundOutlined,
  FileTextOutlined,
  SyncOutlined,
  PartitionOutlined,
};

const resolveIcon = (name) => ICONS[name] || InfoCircleOutlined;

const AboutOverviewPage = ({ data, versionLink }) => {
  const labels = data.sectionLabels || [];

  return (
    <div style={{ ...pageShellStyle }} className="about-shell">
      <main className="about-page">
        {versionLink ? (
          <div className="about-toolbar">
            <Link to={versionLink.to} className="about-version-link">
              {versionLink.label}
            </Link>
          </div>
        ) : null}

        <div className="about-hero">
          <div className="about-hero-eyebrow">
            {data.hero.eyebrow ? (
              <span className="about-eyebrow-label">{data.hero.eyebrow}</span>
            ) : null}
            {data.hero.chips.map((chip) => (
              <span key={chip} className="about-chip">{chip}</span>
            ))}
          </div>
          <h1 className="about-hero-title">{data.hero.title}</h1>
          <p className="about-hero-desc">{data.hero.desc}</p>
          <div className="about-badge-row">
            {data.hero.badges.map(({ icon, label }) => {
              const Icon = resolveIcon(icon);
              return (
                <span key={label} className="about-badge">
                  <Icon />
                  {label}
                </span>
              );
            })}
          </div>
        </div>

        <section>
          <p className="about-section-label">{labels[0]}</p>
          <div className="about-cards-grid">
            {data.capabilities.map(({ icon, title, desc }) => {
              const Icon = resolveIcon(icon);
              return (
                <div key={title} className="about-card">
                  <div className="about-card-icon"><Icon /></div>
                  <p className="about-card-title">{title}</p>
                  <p className="about-card-desc">{desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        <hr className="about-divider" />

        <section>
          <p className="about-section-label">{labels[1]}</p>
          <div className="about-layers">
            {data.layers.map((layer, index) => (
              <div key={layer.name} className="about-layer">
                <div className="about-layer-num">{index + 1}</div>
                <div>
                  <p className="about-layer-name">{layer.name}</p>
                  <p className="about-layer-desc">{layer.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <hr className="about-divider" />

        <section>
          <p className="about-section-label">{labels[2]}</p>
          <div className="about-steps">
            {data.steps.map((step, index) => (
              <div key={step.title} className="about-step">
                <div className="about-step-line">
                  <div className="about-step-dot">{index + 1}</div>
                  {index < data.steps.length - 1 ? <div className="about-step-connector" /> : null}
                </div>
                <div className="about-step-body">
                  <p className="about-step-title">{step.title}</p>
                  <p className="about-step-desc">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <hr className="about-divider" />

        <section>
          <p className="about-section-label">{labels[3]}</p>
          <div className="about-roles-grid">
            {data.roles.map(({ icon, title, access, accessClass, items }) => {
              const Icon = resolveIcon(icon);
              return (
                <div key={title} className="about-role-card">
                  <div className="about-role-header">
                    <Icon className="about-role-icon" />
                    <span className="about-role-title">{title}</span>
                  </div>
                  <span className={`about-role-access ${accessClass}`}>{access}</span>
                  <ul className="about-role-list">
                    {items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        <hr className="about-divider" />

        <section>
          <p className="about-section-label">{labels[4]}</p>
          <div className="about-limits-grid">
            {data.limits.map(({ label, value }) => (
              <div key={label} className="about-limit-item">
                <div className="about-limit-label">{label}</div>
                <div className="about-limit-val">{value}</div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <p className="about-section-label">{labels[5]}</p>
          <div className="about-future-chips">
            {data.roadmap.map(({ icon, label }) => {
              const Icon = resolveIcon(icon);
              return (
                <span key={label} className="about-future-chip">
                  <Icon />
                  {label}
                </span>
              );
            })}
          </div>
        </section>

        <section className="about-starter-kit">
          <div className="about-section-header">
            <p className="about-section-label about-section-label--inline">{labels[6]}</p>
          </div>
          <div className="about-starter-grid">
            {data.starterKit.map(({ title, desc }) => (
              <div key={title} className="about-starter-card">
                <h4 className="about-starter-title">{title}</h4>
                <p className="about-starter-desc">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {data.footer ? (
          <p className="about-footer">{data.footer}</p>
        ) : null}
      </main>
    </div>
  );
};

export default AboutOverviewPage;
